import { Popover, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { lazy, Suspense } from "react";
import { MapGeoJSONFeature } from "react-map-gl/maplibre";
import {
  celsiusToFahrenheit,
  getCurrentWeatherFromCampground,
} from "../../utils/weather";

interface WeatherAveragesProps {
  campground: MapGeoJSONFeature;
}

// The chart bodies live in WeatherCharts.tsx so recharts stays out of the
// initial bundle - these only render behind a hover popover or in the details
// modal. Re-exported here so existing import sites keep working unchanged.
const LazyMeanTemperatureChart = lazy(() =>
  import("./WeatherCharts").then((m) => ({ default: m.MeanTemperatureChart }))
);
const LazyMeanPreciptationChart = lazy(() =>
  import("./WeatherCharts").then((m) => ({ default: m.MeanPreciptationChart }))
);

// A chart is 100px tall; reserve the space so loading it does not shift layout.
const ChartFallback = () => <div style={{ height: 100 }} />;

const MeanTemperatureChart = (props: WeatherAveragesProps) => (
  <Suspense fallback={<ChartFallback />}>
    <LazyMeanTemperatureChart {...props} />
  </Suspense>
);

const MeanPreciptationChart = (props: WeatherAveragesProps) => (
  <Suspense fallback={<ChartFallback />}>
    <LazyMeanPreciptationChart {...props} />
  </Suspense>
);

const WeatherAverages = ({ campground }: WeatherAveragesProps) => {
  const { tmin: currentTmin, tmax: currentTmax } =
    getCurrentWeatherFromCampground(campground);
  const [isOpen, { close, open }] = useDisclosure(false);

  if (!currentTmax || !currentTmin) return null;

  return (
    <Popover width={300} position="right" withArrow shadow="md" opened={isOpen}>
      <Popover.Target>
        <Text
          c="dimmed"
          size="sm"
          style={{
            cursor: "default",
            textDecoration: "underline dashed",
            textWrap: "nowrap",
          }}
          onMouseEnter={open}
          onMouseLeave={close}
        >
          {Math.round(celsiusToFahrenheit(currentTmin))}°F -{" "}
          {Math.round(celsiusToFahrenheit(currentTmax))}°F
        </Text>
      </Popover.Target>
      <Popover.Dropdown p="s" style={{ pointerEvents: "none" }}>
        <Stack gap="0">
          <Text size="sm" pb="xs">
            PRISM Monthly Mean Temperature (°F)
          </Text>
          <MeanTemperatureChart campground={campground} />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export { MeanPreciptationChart, MeanTemperatureChart, WeatherAverages };
